// MainCharts.js
import { useState, useEffect } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
} from "chart.js";
import moment from "moment";
import { FaExpandAlt } from "react-icons/fa";
import IntentsPopup from "./IntentsPopup";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip);

// Colors configuration object
const chartColors = {
  increase: "rgba(50, 235, 50, 0.5)",
  decrease: "rgba(235, 50, 50, 0.5)",
  defaultCategories: "rgba(56, 126, 252, 0.8)",
  defaultIntents: "rgba(56, 126, 252, 0.8)",
  //breakoutIntents: 'rgba(232, 121, 249, 0.8)',
  breakoutIntents: "rgba(56, 126, 252, 0.8)",
  calls: "rgba(56, 126, 252, 0.6)",
};

// Define the stackedBarLabelsPlugin
const stackedBarLabelsPlugin = {
  id: "stackedBarLabelsPlugin",
  afterDatasetsDraw(chart) {
    const { ctx, data, scales, options } = chart;
    const percentageChanges =
      options.plugins.percentageChangePlugin.percentageChanges;

    data.datasets[0].data.forEach((value, index) => {
      const total = data.datasets.reduce(
        (sum, dataset) => sum + dataset.data[index],
        0
      );
      const xStart = scales.x.getPixelForValue(0);
      const xEnd = scales.x.getPixelForValue(total);
      const barWidth = xEnd - xStart;
      const yPos = scales.y.getPixelForValue(data.labels[index]);

      ctx.save();
      ctx.textBaseline = "middle";
      ctx.font = "12px Lato";

      const percentageChange = parseFloat(percentageChanges[index]);
      const displayText = `${percentageChange.toFixed(1)}%`;

      if (barWidth > 200) {
        ctx.textAlign = "center";
        ctx.fillStyle = "white";
        ctx.fillText(displayText, (xStart + xEnd) / 2, yPos);
      } else {
        ctx.textAlign = "left";
        ctx.fillStyle = "black";
        ctx.fillText(displayText, xEnd + 5, yPos);
      }

      if (Math.abs(percentageChange) > 20) {
        ctx.font = "bold 12px Lato";
        ctx.fillText(
          displayText,
          barWidth > 200 ? (xStart + xEnd) / 2 : xEnd + 5,
          yPos
        );
      }

      ctx.restore();
    });
  },
};

// Define the barLabelsPlugin
const barLabelsPlugin = {
  id: "barLabelsPlugin",
  afterDatasetsDraw(chart) {
    const { ctx, data, scales } = chart;
    data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      meta.data.forEach((element, index) => {
        const value = dataset.data[index];
        const xScale = scales.x;

        const yPos = element.y;
        const xStart = xScale.getPixelForValue(0);
        const xEnd = element.x;
        const barWidth = Math.abs(xEnd - xStart);

        ctx.save();
        ctx.font = "12px Lato";

        const percentageGain = value;
        const currentCount = data.labels[index].currentCount;
        let strValue = `${percentageGain.toFixed(1)}% (${currentCount})`;

        if (barWidth < 40) {
          ctx.textAlign = "left";
          ctx.textBaseline = "middle";
          ctx.fillStyle = "black";
          ctx.fillText(strValue, xEnd + 2, yPos);
        } else {
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillStyle = "white";
          ctx.fillText(strValue, (xStart + xEnd) / 2, yPos);
        }

        ctx.restore();
      });
    });
  },
};

// Define the simpleValueLabelsPlugin
const simpleValueLabelsPlugin = {
  id: "simpleValueLabelsPlugin",
  afterDatasetsDraw(chart) {
    const { ctx, data, scales } = chart;
    data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      meta.data.forEach((element, index) => {
        const value = dataset.data[index];
        const xScale = scales.x;

        const yPos = element.y;
        const xStart = xScale.getPixelForValue(0);
        const xEnd = element.x;
        const barWidth = xEnd - xStart;

        ctx.save();
        ctx.font = "12px Lato";

        if (barWidth < 20) {
          ctx.textAlign = "left";
          ctx.textBaseline = "middle";
          ctx.fillStyle = "black";
          ctx.fillText(value, xEnd + 2, yPos);
        } else {
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillStyle = "white";
          ctx.fillText(value, xStart + barWidth / 2, yPos);
        }

        ctx.restore();
      });
    });
  },
};

const groupDataByWeek = (labels, rowCounts, averageCustomerSentiments) => {
  const weekData = {};

  labels.forEach((date, index) => {
    // Get the start of the week (Sunday) for the current date
    const weekStart = moment(date).startOf("week").format("YYYY-MM-DD");
    const displayDate = moment(weekStart).format("MM/DD/YYYY");

    if (!weekData[displayDate]) {
      weekData[displayDate] = {
        totalCalls: 0,
        sentimentSum: 0,
        sentimentCount: 0,
      };
    }

    weekData[displayDate].totalCalls += rowCounts[index] || 0;

    // Only add to sentiment if it's a valid number
    if (
      averageCustomerSentiments[index] !== null &&
      !isNaN(averageCustomerSentiments[index])
    ) {
      weekData[displayDate].sentimentSum += averageCustomerSentiments[index];
      weekData[displayDate].sentimentCount += 1;
    }
  });

  const sortedWeeks = Object.keys(weekData).sort(
    (a, b) =>
      moment(a, "MM/DD/YYYY").valueOf() - moment(b, "MM/DD/YYYY").valueOf()
  );

  const groupedLabels = sortedWeeks;
  const groupedRowCounts = sortedWeeks.map((week) => weekData[week].totalCalls);
  const groupedSentiments = sortedWeeks.map((week) =>
    weekData[week].sentimentCount > 0
      ? weekData[week].sentimentSum / weekData[week].sentimentCount
      : 0
  );

  return {
    labels: groupedLabels,
    rowCounts: groupedRowCounts,
    averageCustomerSentiments: groupedSentiments,
  };
};

const groupDataByMonth = (labels, rowCounts, averageCustomerSentiments) => {
  const monthData = {};

  labels.forEach((date, index) => {
    const monthYear = moment(date).format("MMM YYYY");

    if (!monthData[monthYear]) {
      monthData[monthYear] = {
        totalCalls: 0,
        sentimentSum: 0,
        sentimentCount: 0,
      };
    }

    monthData[monthYear].totalCalls += rowCounts[index] || 0;

    // Only add to sentiment if it's a valid number
    if (
      averageCustomerSentiments[index] !== null &&
      !isNaN(averageCustomerSentiments[index])
    ) {
      monthData[monthYear].sentimentSum += averageCustomerSentiments[index];
      monthData[monthYear].sentimentCount += 1;
    }
  });

  const sortedMonths = Object.keys(monthData).sort(
    (a, b) => moment(a, "MMM YYYY").valueOf() - moment(b, "MMM YYYY").valueOf()
  );

  const groupedLabels = sortedMonths;
  const groupedRowCounts = sortedMonths.map(
    (month) => monthData[month].totalCalls
  );
  const groupedSentiments = sortedMonths.map((month) =>
    monthData[month].sentimentCount > 0
      ? monthData[month].sentimentSum / monthData[month].sentimentCount
      : 0
  );

  return {
    labels: groupedLabels,
    rowCounts: groupedRowCounts,
    averageCustomerSentiments: groupedSentiments,
  };
};

function MainCharts({
  topCategories = [],
  topIntents = [],
  intentCounts = {},
  baselines = null,
  loadBaselinesIfNeeded,
  dateGroupData = { labels: [], rowCounts: [], averageCustomerSentiments: [] },
  dateGroupType = "day",
  showTrends = false,
  dateRange,
}) {
  const [showIntentsPopup, setShowIntentsPopup] = useState(false);
  const [processedDateGroupData, setProcessedDateGroupData] =
    useState(dateGroupData);
  const [effectiveDateGroupType, setEffectiveDateGroupType] =
    useState(dateGroupType);
  const [loadingIntentsData, setLoadingIntentsData] = useState(false);

  useEffect(() => {
    // Group data based on date range
    if (
      !showTrends &&
      dateGroupData &&
      dateGroupData.labels &&
      dateGroupData.labels.length > 0
    ) {
      if (dateRange === "thisYear") {
        const groupedData = groupDataByWeek(
          dateGroupData.labels,
          dateGroupData.rowCounts,
          dateGroupData.averageCustomerSentiments
        );
        setProcessedDateGroupData(groupedData);
        setEffectiveDateGroupType("week");
      } else if (dateRange === "all") {
        const groupedData = groupDataByMonth(
          dateGroupData.labels,
          dateGroupData.rowCounts,
          dateGroupData.averageCustomerSentiments
        );
        setProcessedDateGroupData(groupedData);
        setEffectiveDateGroupType("month");
      } else {
        setProcessedDateGroupData(dateGroupData);
        setEffectiveDateGroupType(dateGroupType);
      }
    }
  }, [dateRange, dateGroupData, showTrends, dateGroupType]);

  if (showTrends && !baselines) {
    showTrends = false;
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    plugins: {
      legend: {
        display: false,
      },
    },
    interaction: {
      mode: "index",
      intersect: true,
    },
  };

  // Categories chart data and options
  let categoriesChartData;
  let categoriesChartOptions;

  if (showTrends && baselines && "avgCategoryFrequencies" in baselines) {
    const categories = topCategories
      .filter((item) => item.category_name !== null)
      .map((item) => item.category_name);
    const baselineData = [];
    const deltaData = [];
    const deltaBackgroundColors = [];
    const percentageChanges = [];
    const totalValues = [];
    const currentCounts = [];
    const baselineCounts = [];

    categories.forEach((category, index) => {
      const currentCount = topCategories[index]?.call_count || 0;
      const baselineCount = baselines.avgCategoryFrequencies[category] || 0;

      const minCount = Math.min(currentCount, baselineCount);
      const change = Math.abs(currentCount - baselineCount);

      baselineData.push(minCount);
      deltaData.push(change);

      const isIncrease = currentCount >= baselineCount;

      deltaBackgroundColors.push(
        isIncrease ? chartColors.increase : chartColors.decrease
      );

      let percentageChange = 0;
      if (baselineCount !== 0) {
        percentageChange =
          ((currentCount - baselineCount) / baselineCount) * 100;
      } else if (currentCount !== 0) {
        percentageChange = 100;
      }

      percentageChanges.push(percentageChange.toFixed(1));
      totalValues.push(minCount + change);
      currentCounts.push(currentCount);
      baselineCounts.push(baselineCount);
    });

    categoriesChartData = {
      labels: categories,
      datasets: [
        {
          label: "Baseline",
          data: baselineData,
          backgroundColor: chartColors.defaultCategories,
          stack: "Stack 0",
          tooltip: { enabled: false },
        },
        {
          label: "Change",
          data: deltaData,
          backgroundColor: deltaBackgroundColors,
          stack: "Stack 0",
        },
      ],
    };

    categoriesChartOptions = {
      ...chartOptions,
      plugins: {
        ...chartOptions.plugins,
        title: {
          display: true,
          text: "Categories",
        },
        tooltip: {
          mode: "nearest",
          intersect: false,
          axis: "y",
          filter: function (tooltipItem) {
            return tooltipItem.datasetIndex === 1;
          },
          callbacks: {
            labelColor: function (context) {
              return {
                borderColor: "transparent",
                backgroundColor: chartColors.defaultCategories,
              };
            },
            label: function (context) {
              const index = context.dataIndex;
              const pluginOptions =
                context.chart.options.plugins.percentageChangePlugin;
              const currentCount = Math.round(
                pluginOptions.currentCounts[index]
              );
              const baselineCount = Math.round(
                pluginOptions.baselineCounts[index]
              );
              const percentChange = Math.round(
                pluginOptions.percentageChanges[index]
              );
              return [
                `Current: ${currentCount}`,
                `Previous: ${baselineCount}`,
                `Change: ${percentChange >= 0 ? "+" : ""}${percentChange}%`,
              ];
            },
          },
        },
        percentageChangePlugin: {
          percentageChanges,
          totalValues,
          currentCounts,
          baselineCounts,
        },
      },
      scales: {
        x: {
          stacked: true,
          beginAtZero: true,
          title: {
            display: true,
            text: "Count",
          },
        },
        y: {
          stacked: true,
          title: {
            display: false, // Removed y-axis title
            text: "Categories",
          },
          ticks: {
            autoSkip: false,
          },
        },
      },
    };
  } else {
    categoriesChartData = {
      labels: topCategories
        .filter((item) => item.category_name !== null)
        .map((item) => item.category_name),
      datasets: [
        {
          label: "Categories",
          data: topCategories.map((item) => item.call_count),
          backgroundColor: topCategories.map(
            () => chartColors.defaultCategories
          ),
        },
      ],
    };

    categoriesChartOptions = {
      ...chartOptions,
      plugins: {
        ...chartOptions.plugins,
        title: {
          display: true,
          text: "Categories",
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          title: {
            display: true,
            text: "Count",
          },
        },
        y: {
          title: {
            display: false, // Removed y-axis title
            text: "Categories",
          },
          ticks: {
            autoSkip: false,
          },
        },
      },
    };
  }

  // Intents chart data and options
  let intentsChartData;
  let intentsChartOptions;

  if (showTrends && baselines && "avgIntentFrequencies" in baselines) {
    const intents = topIntents
      .filter((item) => item.intent !== null)
      .map((item) => item.intent);
    const baselineData = [];
    const deltaData = [];
    const deltaBackgroundColors = [];
    const percentageChanges = [];
    const totalValues = [];
    const currentCounts = [];
    const baselineCounts = [];

    intents.forEach((intent, index) => {
      const currentCount = topIntents[index]?.call_count || 0;
      const baselineCount = baselines.avgIntentFrequencies[intent] || 0;

      const minCount = Math.min(currentCount, baselineCount);
      const change = Math.abs(currentCount - baselineCount);

      baselineData.push(minCount);
      deltaData.push(change);

      const isIncrease = currentCount >= baselineCount;

      deltaBackgroundColors.push(
        isIncrease ? chartColors.increase : chartColors.decrease
      );

      let percentageChange = 0;
      if (baselineCount !== 0) {
        percentageChange =
          ((currentCount - baselineCount) / baselineCount) * 100;
      } else if (currentCount !== 0) {
        percentageChange = 100;
      }

      percentageChanges.push(percentageChange.toFixed(1));
      totalValues.push(minCount + change);
      currentCounts.push(currentCount);
      baselineCounts.push(baselineCount);
    });

    intentsChartData = {
      labels: intents,
      datasets: [
        {
          label: "Baseline",
          data: baselineData,
          backgroundColor: chartColors.defaultIntents,
          stack: "Stack 0",
          tooltip: { enabled: false },
        },
        {
          label: "Change",
          data: deltaData,
          backgroundColor: deltaBackgroundColors,
          stack: "Stack 0",
        },
      ],
    };

    intentsChartOptions = {
      ...chartOptions,
      plugins: {
        ...chartOptions.plugins,
        title: {
          display: true,
          text: "Top 10 Intents",
          font: {
            size: 16,
          },
          padding: {
            top: 10,
            bottom: 30,
          },
        },
        tooltip: {
          mode: "nearest",
          intersect: false,
          axis: "y",
          filter: function (tooltipItem) {
            return tooltipItem.datasetIndex === 1;
          },
          callbacks: {
            labelColor: function (context) {
              return {
                borderColor: "transparent",
                backgroundColor: chartColors.defaultIntents, // or chartColors.defaultIntents for intents chart
              };
            },
            label: function (context) {
              const index = context.dataIndex;
              const pluginOptions =
                context.chart.options.plugins.percentageChangePlugin;
              const currentCount = Math.round(
                pluginOptions.currentCounts[index]
              );
              const baselineCount = Math.round(
                pluginOptions.baselineCounts[index]
              );
              const percentChange = Math.round(
                pluginOptions.percentageChanges[index]
              );
              return [
                `Current: ${currentCount}`,
                `Previous: ${baselineCount}`,
                `Change: ${percentChange >= 0 ? "+" : ""}${percentChange}%`,
              ];
            },
          },
        },
        percentageChangePlugin: {
          percentageChanges,
          totalValues,
          currentCounts,
          baselineCounts,
        },
      },
      scales: {
        x: {
          stacked: true,
          beginAtZero: true,
          title: {
            display: true,
            text: "Count",
          },
        },
        y: {
          stacked: true,
          title: {
            display: false, // Removed y-axis title
            text: "Intents",
          },
          ticks: {
            autoSkip: false,
          },
        },
      },
    };
  } else {
    intentsChartData = {
      labels: topIntents
        .filter((item) => item.intent !== null)
        .map((item) => item.intent),
      datasets: [
        {
          label: "Intents",
          data: topIntents.map((item) => item.call_count),
          backgroundColor: topIntents.map(() => chartColors.defaultIntents),
        },
      ],
    };

    intentsChartOptions = {
      ...chartOptions,
      plugins: {
        ...chartOptions.plugins,
        title: {
          display: true,
          text: "Top 10 Intents",
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          title: {
            display: true,
            text: "Count",
          },
        },
        y: {
          title: {
            display: false, // Removed y-axis title
            text: "Intents",
          },
          ticks: {
            autoSkip: false,
          },
        },
      },
    };
  }

  // Breakout Intents chart data and options
  let breakoutIntentsChartData = null;
  let breakoutIntentsChartOptions = null;

  if (showTrends && baselines && "avgIntentFrequencies" in baselines) {
    const intentsData = [];
    for (const intent in intentCounts) {
      const currentCount = intentCounts[intent] || 0;
      const averageCount = baselines.avgIntentFrequencies[intent] || 0;

      let percentageGain = 0;
      if (averageCount !== 0) {
        percentageGain = ((currentCount - averageCount) / averageCount) * 100;
      } else if (currentCount !== 0) {
        percentageGain = 100;
      }
      intentsData.push({
        intent,
        percentageGain,
        currentCount,
        averageCount,
      });
    }

    intentsData.sort((a, b) => b.percentageGain - a.percentageGain);

    const topBreakoutIntents = intentsData.slice(0, 10);

    breakoutIntentsChartData = {
      labels: topBreakoutIntents.map((item) => ({
        label: item.intent,
        currentCount: item.currentCount,
      })),
      datasets: [
        {
          label: "Percentage Gain",
          data: topBreakoutIntents.map((item) => item.percentageGain),
          backgroundColor: chartColors.breakoutIntents,
        },
      ],
    };

    breakoutIntentsChartOptions = {
      ...chartOptions,
      indexAxis: "y",
      plugins: {
        ...chartOptions.plugins,
        legend: {
          display: false,
        },
        title: {
          display: true,
          text: "Breakout Intents",
        },
        tooltip: {
          mode: "nearest",
          intersect: false,
          axis: "y",
          callbacks: {
            title: function (context) {
              const index = context[0].dataIndex;
              const data = topBreakoutIntents[index];
              return data.intent;
            },
            label: function (context) {
              const index = context.dataIndex;
              const data = topBreakoutIntents[index];
              const percentageGain = data.percentageGain.toFixed(1);
              const currentCount = data.currentCount;
              const averageCount = data.averageCount.toFixed(1);
              return [
                `Current: ${currentCount}`,
                `Average: ${averageCount}`,
                `Change: ${percentageGain >= 0 ? "+" : ""}${percentageGain}%`,
              ];
            },
          },
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          title: {
            display: true,
            text: "Percentage Gain",
          },
        },
        y: {
          title: {
            display: false,
            text: "Intents",
          },
          ticks: {
            autoSkip: false,
            callback: function (value, index) {
              return this.getLabelForValue(value).label;
            },
          },
        },
      },
    };
  }

  // Calls chart for showTrends = false
  let callsChartData = null;
  let callsChartOptions = null;
  let averageCustomerSentimentChartData = null;
  let averageCustomerSentimentChartOptions = null;

  if (
    !showTrends &&
    processedDateGroupData &&
    processedDateGroupData.labels &&
    processedDateGroupData.labels.length > 0
  ) {
    callsChartData = {
      labels: processedDateGroupData.labels,
      datasets: [
        {
          label: "Contacts",
          data: processedDateGroupData.rowCounts,
          backgroundColor: chartColors.calls,
        },
      ],
    };

    callsChartOptions = {
      ...chartOptions,
      indexAxis: "x",
      scales: {
        x: {
          title: {
            display: true,
            text:
              effectiveDateGroupType === "hour"
                ? "Time"
                : effectiveDateGroupType === "month"
                ? "Month"
                : effectiveDateGroupType === "week"
                ? "Week Starting"
                : "Date",
          },
          ticks: {
            // If there are too many dates
            autoSkip: true,
            maxRotation: 90,
            minRotation: 45,
            callback: function (value) {
              const label = this.getLabelForValue(value);
              if (effectiveDateGroupType === "hour") {
                return moment(label).format("h:mm A");
              } else if (effectiveDateGroupType === "month") {
                return label; // Month format is already in MMM YYYY
              } else if (effectiveDateGroupType === "week") {
                return label; // Week format is already in MM/DD/YYYY
              } else {
                return moment(label).format("MM/DD/YYYY");
              }
            },
          },
        },
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: "Contacts",
          },
        },
      },
      plugins: {
        ...chartOptions.plugins,
        tooltip: {
          // Makes hovering above very short bars easier
          intersect: false,
          callbacks: {
            title: function (tooltipItems) {
              const label = tooltipItems[0].label;
              if (effectiveDateGroupType === "hour") {
                return moment(label).format("h:mm A");
              } else if (effectiveDateGroupType === "month") {
                return label; // Month format is already in MMM YYYY
              } else if (effectiveDateGroupType === "week") {
                return `Week of ${label}`;
              } else {
                return moment(label).format("MM/DD/YYYY");
              }
            },
          },
        },
        title: {
          display: true,
          text: `Contacts by ${
            effectiveDateGroupType.charAt(0).toUpperCase() +
            effectiveDateGroupType.slice(1)
          }`,
        },
      },
    };

    averageCustomerSentimentChartData = {
      labels: processedDateGroupData.labels,
      datasets: [
        {
          label: "Average Customer Sentiment",
          data: processedDateGroupData.averageCustomerSentiments,
          backgroundColor: chartColors.calls,
        },
      ],
    };

    averageCustomerSentimentChartOptions = {
      ...chartOptions,
      indexAxis: "x",
      scales: {
        x: {
          title: {
            display: true,
            text:
              effectiveDateGroupType === "hour"
                ? "Time"
                : effectiveDateGroupType === "month"
                ? "Month"
                : effectiveDateGroupType === "week"
                ? "Week Starting"
                : "Date",
          },
          ticks: {
            // If there are too many dates
            autoSkip: true,
            maxRotation: 90,
            minRotation: 45,
            callback: function (value) {
              const label = this.getLabelForValue(value);
              if (effectiveDateGroupType === "hour") {
                return moment(label).format("h:mm A");
              } else if (effectiveDateGroupType === "month") {
                return label; // Month format is already in MMM YYYY
              } else if (effectiveDateGroupType === "week") {
                return label; // Week format is already in MM/DD/YYYY
              } else {
                return moment(label).format("MM/DD/YYYY");
              }
            },
          },
        },
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: "Sentiment",
          },
        },
      },
      plugins: {
        ...chartOptions.plugins,
        tooltip: {
          // Makes hovering above very short bars easier
          intersect: false,
          callbacks: {
            title: function (tooltipItems) {
              const label = tooltipItems[0].label;
              if (effectiveDateGroupType === "hour") {
                return moment(label).format("h:mm A");
              } else if (effectiveDateGroupType === "month") {
                return label; // Month format is already in MMM YYYY
              } else if (effectiveDateGroupType === "week") {
                return `Week of ${label}`;
              } else {
                return moment(label).format("MM/DD/YYYY");
              }
            },
          },
        },
        title: {
          display: true,
          text: `Average Customer Sentiment by ${
            effectiveDateGroupType.charAt(0).toUpperCase() +
            effectiveDateGroupType.slice(1)
          }`,
        },
      },
    };
  }

  const chartHeight = 350;

  return (
      <div className="flex flex-wrap mb-8 container mx-auto ">
      <div
        className={`w-full ${showTrends ? "md:w-1/3" : "md:w-1/4"} p-2`}
        style={{ height: `${chartHeight}px` }}
      >
        <Bar
          key={`categoriesChart-${showTrends}`}
          options={categoriesChartOptions}
          data={categoriesChartData}
          plugins={
            showTrends ? [stackedBarLabelsPlugin] : [simpleValueLabelsPlugin]
          }
        />
      </div>
      <div
        className={`w-full ${showTrends ? "md:w-1/3" : "md:w-1/4"} p-2`}
        style={{ height: `${chartHeight}px` }}
      >
        <div className="relative h-full">
          <Bar
            className="h-full"
            key={`intentsChart-${showTrends}`}
            options={intentsChartOptions}
            data={intentsChartData}
            plugins={[
              ...(showTrends
                ? [stackedBarLabelsPlugin]
                : [simpleValueLabelsPlugin]),
            ]}
          />
          <button
            className="absolute top-2 right-2 p-1 text-usan-primary hover:text-usan-primary focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={async () => {
              try {
                setLoadingIntentsData(true);  // Start loading
                await loadBaselinesIfNeeded();
                setShowIntentsPopup(true);
              } catch (error) {
                console.error("Failed to load baselines for intents popup:", error);
                alert("Failed to load baseline data. Please try again.");
              } finally {
                setLoadingIntentsData(false);  // End loading regardless of success/failure
              }
            }}
            disabled={loadingIntentsData}  // Disable button while loading
            title={loadingIntentsData ? "Loading..." : "View all intents"}
          >
            {loadingIntentsData ? (
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-usan-primary border-t-transparent"></div>
            ) : (
              <FaExpandAlt className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>
      {!showTrends && callsChartData && (
        <div
          className="w-full md:w-1/4 p-2"
          style={{ height: `${chartHeight}px` }}
        >
          <Bar options={callsChartOptions} data={callsChartData} />
        </div>
      )}
      {!showTrends && averageCustomerSentimentChartData && (
        <div
          className="w-full md:w-1/4 p-2"
          style={{ height: `${chartHeight}px` }}
        >
          <Bar
            options={averageCustomerSentimentChartOptions}
            data={averageCustomerSentimentChartData}
          />
        </div>
      )}
      {showTrends && breakoutIntentsChartData && (
        <div
          className="w-full md:w-1/3 p-2"
          style={{ height: `${chartHeight}px` }}
        >
          <Bar
            options={breakoutIntentsChartOptions}
            data={breakoutIntentsChartData}
            plugins={[barLabelsPlugin]}
          />
        </div>
      )}

      {showIntentsPopup && loadBaselinesIfNeeded && (
        <IntentsPopup
          intentCounts={intentCounts}
          baselines={baselines}
          loadBaselinesIfNeeded={loadBaselinesIfNeeded}
          onClose={() => setShowIntentsPopup(false)}
        />
      )}
    </div>
  );
}

export default MainCharts;
